/*
  hr-perf-core.js — shared main-thread budget manager.

  Problem it solves: the theme was running dozens of independent
  IntersectionObservers, per-section rAF pumps and setInterval timers that kept
  ticking while their section was far off-screen or the tab was in the
  background. The main thread never reached an idle period (Lighthouse
  NO_TTI_CPU_IDLE_PERIOD on every page).

  This file changes NO visible behaviour. Everything it exposes runs the exact
  same callbacks as before -- it just refuses to run them while the owning
  element is off-screen or the tab is hidden, and resumes them immediately when
  the element scrolls back into view.

  API (all optional -- every call site degrades gracefully if this file is
  missing, see the guard pattern used at the call sites):

    HRPerf.onVisible(el, cb, opts)   -> unobserve()
        Fires cb(true) when el enters the viewport (default rootMargin 200px),
        cb(false) when it leaves. One shared IntersectionObserver per unique
        rootMargin/threshold pair instead of one per section.

    HRPerf.whenNear(el, cb, margin)  -> unobserve()
        One-shot: fires cb() once when el is within ~1.5 viewports. Used to
        defer below-fold section init off the DOMContentLoaded critical path.

    HRPerf.tick(fn, el)              -> stop()
        Subscribes fn(now) to ONE shared requestAnimationFrame loop. If el is
        given, fn only runs while el is on-screen. The shared rAF loop stops
        entirely (cancelAnimationFrame) when no subscriber is currently
        runnable -- that is what lets the main thread idle.

    HRPerf.interval(fn, ms, el)      -> stop()
        Drop-in setInterval replacement that is paused while the tab is hidden
        and (if el given) while el is off-screen. On resume it fires
        immediately if more than `ms` has elapsed, so time-driven UI
        (countdowns, rotators) never shows a stale value.

    HRPerf.visible                   -> boolean (tab foreground)
*/
(function () {
  'use strict';
  if (window.HRPerf) return;

  var hasIO = typeof IntersectionObserver === 'function';

  /* ---------------------------------------------------------------- tab */
  var tabVisible = document.visibilityState !== 'hidden';
  var tabListeners = [];
  document.addEventListener(
    'visibilitychange',
    function () {
      var next = document.visibilityState !== 'hidden';
      if (next === tabVisible) return;
      tabVisible = next;
      for (var i = 0; i < tabListeners.length; i++) {
        try {
          tabListeners[i](tabVisible);
        } catch (e) {}
      }
    },
    false
  );

  /* -------------------------------------------- shared visibility registry */
  // One IntersectionObserver per unique config string, shared by every caller
  // that wants that config. Callbacks live on the element in a WeakMap-ish map.
  var observers = {};
  var cbKey = '__hrPerfCbs';

  function getObserver(key, options) {
    if (observers[key]) return observers[key];
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var list = entry.target[cbKey] && entry.target[cbKey][key];
        if (!list) continue;
        // copy: a callback may unobserve itself
        var snapshot = list.slice();
        for (var j = 0; j < snapshot.length; j++) {
          try {
            snapshot[j](entry.isIntersecting, entry);
          } catch (e) {}
        }
      }
    }, options);
    observers[key] = io;
    return io;
  }

  function onVisible(el, cb, opts) {
    if (!el || typeof cb !== 'function') return function () {};
    if (!hasIO) {
      // No IO support: behave exactly as the old always-on code did.
      cb(true);
      return function () {};
    }
    opts = opts || {};
    var margin = opts.rootMargin || '200px 0px 200px 0px';
    var threshold = opts.threshold == null ? 0 : opts.threshold;
    var key = margin + '|' + threshold;
    var io = getObserver(key, { rootMargin: margin, threshold: threshold });

    if (!el[cbKey]) el[cbKey] = {};
    if (!el[cbKey][key]) {
      el[cbKey][key] = [];
      io.observe(el);
    }
    el[cbKey][key].push(cb);

    return function () {
      var list = el[cbKey] && el[cbKey][key];
      if (!list) return;
      var idx = list.indexOf(cb);
      if (idx > -1) list.splice(idx, 1);
      if (!list.length) {
        io.unobserve(el);
        delete el[cbKey][key];
      }
    };
  }

  function whenNear(el, cb, margin) {
    if (!el || typeof cb !== 'function') return function () {};
    if (!hasIO) {
      cb();
      return function () {};
    }
    var done = false;
    var off = onVisible(
      el,
      function (isVisible) {
        if (!isVisible || done) return;
        done = true;
        off();
        cb();
      },
      { rootMargin: margin || '150% 0px 150% 0px' }
    );
    return off;
  }

  /* ------------------------------------------------------- shared rAF loop */
  var subs = [];
  var rafId = 0;

  function runnable(s) {
    return s.active && (!s.el || s.visible);
  }

  function anyRunnable() {
    if (!tabVisible) return false;
    for (var i = 0; i < subs.length; i++) if (runnable(subs[i])) return true;
    return false;
  }

  function pump(now) {
    rafId = 0;
    var snapshot = subs.slice();
    for (var i = 0; i < snapshot.length; i++) {
      var s = snapshot[i];
      if (!runnable(s)) continue;
      try {
        s.fn(now);
      } catch (e) {}
    }
    schedule();
  }

  function schedule() {
    if (rafId) return;
    if (!anyRunnable()) return; // <- the main thread is allowed to go idle
    rafId = requestAnimationFrame(pump);
  }

  tabListeners.push(function (isVisible) {
    if (isVisible) schedule();
    else if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
  });

  function tick(fn, el) {
    if (typeof fn !== 'function') return function () {};
    var s = { fn: fn, el: el || null, visible: !el, active: true };
    var off = null;
    if (el) {
      off = onVisible(el, function (isVisible) {
        s.visible = isVisible;
        if (isVisible) schedule();
      });
    }
    subs.push(s);
    schedule();
    return function () {
      s.active = false;
      var idx = subs.indexOf(s);
      if (idx > -1) subs.splice(idx, 1);
      if (off) off();
    };
  }

  /* -------------------------------------------------- gated setInterval */
  function interval(fn, ms, el) {
    if (typeof fn !== 'function') return function () {};
    var id = 0;
    var last = Date.now();
    var visible = !el;
    var stopped = false;

    function fire() {
      last = Date.now();
      try {
        fn();
      } catch (e) {}
    }

    function start(catchUp) {
      if (stopped || id) return;
      if (!tabVisible || !visible) return;
      // Time-driven UI must not show a stale value after a pause.
      if (catchUp && Date.now() - last >= ms) fire();
      id = setInterval(fire, ms);
    }

    function stopTimer() {
      if (!id) return;
      clearInterval(id);
      id = 0;
    }

    tabListeners.push(function (isVisible) {
      if (isVisible) start(true);
      else stopTimer();
    });

    var off = null;
    if (el) {
      off = onVisible(el, function (isVisible) {
        visible = isVisible;
        if (isVisible) start(true);
        else stopTimer();
      });
      // If IO is unsupported onVisible already called back with true.
    }

    start(false);

    return function () {
      stopped = true;
      stopTimer();
      if (off) off();
    };
  }

  /* ------------------------------------------------- post-paint deferral */
  /*
    HRDefer(fn) — run fn AFTER first paint instead of during HTML parse.

    The theme ships several large inline IIFEs (mobile menu, quiz popup, cart
    sheet motion, headline animation, announcement marquee). Parsing AND
    executing them blocks the first paint, which showed up as ~1.2s of LCP
    render-delay. None of them paint above-the-fold pixels: their markup is
    already server-rendered, the script only wires up behaviour.

    Ordering guarantee: fn always runs after DOMContentLoaded, and callbacks
    run in the order they were registered (one shared idle callback drains a
    FIFO queue), so scripts that publish globals consumed by later-registered
    scripts keep their relative order. Falls back to setTimeout where
    requestIdleCallback is unavailable (Safari < 17).
  */
  var deferQueue = [];
  var deferScheduled = false;

  function drainDefer() {
    deferScheduled = false;
    var q = deferQueue;
    deferQueue = [];
    for (var i = 0; i < q.length; i++) {
      try {
        q[i]();
      } catch (e) {}
    }
  }

  function scheduleDrain() {
    if (deferScheduled) return;
    deferScheduled = true;
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(drainDefer, { timeout: 500 });
    } else {
      setTimeout(drainDefer, 1);
    }
  }

  function defer(fn) {
    if (typeof fn !== 'function') return;
    deferQueue.push(fn);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleDrain, { once: true });
    } else {
      scheduleDrain();
    }
  }

  window.HRDefer = defer;

  window.HRPerf = {
    defer: defer,
    onVisible: onVisible,
    whenNear: whenNear,
    tick: tick,
    interval: interval,
    get visible() {
      return tabVisible;
    }
  };
})();
